import { AfterViewInit, Directive, ElementRef, inject, input, model } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { WINDOW } from '../../features/calendar/util/browser-window-ref';

@Directive({
  selector: '[appCollapsible]',
  exportAs: 'appCollapsible',
  standalone: true,
})
export class CollapsibleDirective implements AfterViewInit {
  // Signals
  duration = input<number>();
  animType = input<string>();
  expanded = model(false);

  // DI
  private readonly el = inject(ElementRef<HTMLElement>).nativeElement;
  private readonly window = inject<Window>(WINDOW);

  // Internal states
  private _viewReady = false;

  constructor() {
    toObservable(this.expanded).subscribe((oldVal) => {
      const next = !!oldVal;

      if (!this._viewReady) {
        return;
      }

      if (next) {
        this.expand();
      } else {
        this.collapse();
      }
    });
  }

  ngAfterViewInit(): void {
    this._viewReady = true;
  }

  public expand(): void {
    if (this.isReducedMotion()) {
      // No animation for reduced motion
      this.expanded.set(true);
      return;
    }

    const startH = this.el.getBoundingClientRect().height; // usually 0
    const endH = this.el.scrollHeight;

    this.setInlineStyle({
      height: `${startH}px`,
      willChange: 'height',
      transition: `height ${this.getDurationInMs()}ms ${this.getAnimType()}`,
    });

    // force reflow
    this.el.getBoundingClientRect();

    // animate to full height
    this.el.style.height = `${endH}px`;

    const onEnd = () => {
      this.el.removeEventListener('transitionend', onEnd);
      this.clearTransition();
      // Keep layout natural when open
      this.el.style.height = 'auto';
      this.expanded.set(true);
    };
    this.el.addEventListener('transitionend', onEnd, { once: true });
  }

  public collapse(): void {
    if (this.isReducedMotion()) {
      this.expanded.set(false);
      return;
    }

    // If currently auto, snap to the exact pixel height first
    const currentHeight = this.el.getBoundingClientRect().height || this.el.scrollHeight;
    this.setInlineStyle({
      height: `${currentHeight}px`,
      willChange: 'height',
      transition: `height ${this.getDurationInMs()}ms ${this.getAnimType()}`,
    });

    // force reflow
    this.el.getBoundingClientRect();

    // animate to 0
    this.el.style.height = '0px';

    const onEnd = () => {
      this.el.removeEventListener('transitionend', onEnd);
      this.clearTransition();
      this.el.style.height = '0px';
      this.expanded.set(false);
    };
    this.el.addEventListener('transitionend', onEnd, { once: true });
  }

  /** Internal helpers */
  private clearTransition() {
    this.el.style.transition = '';
    this.el.style.willChange = '';
  }

  private setInlineStyle(map: Partial<CSSStyleDeclaration>) {
    for (const [k, v] of Object.entries(map)) {
      this.el.style[k] = v!;
    }
  }

  private isReducedMotion(): boolean {
    return this.window?.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private getDurationInMs(): number {
    if (this.duration()) {
      return this.duration();
    }
    const css = getComputedStyle(this.el);
    return parseFloat(css.getPropertyValue('--collapse-duration').trim());
  }

  private getAnimType() {
    if (this.animType()) {
      return this.animType();
    }
    const css = getComputedStyle(this.el);
    return css.getPropertyValue('--collapse-function').trim();
  }
}
